<template>
  <div>
    <Head title="Reportes" />
    <h1 class="mb-8 text-3xl font-bold">Reportes y Estadísticas</h1>

    <!-- Formulario de generación de reportes -->
    <div class="bg-white p-6 rounded-lg shadow-sm mb-6">
      <h2 class="text-xl font-semibold mb-4">Generar Reporte</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <text-input 
          v-model="form.from" 
          label="Fecha inicial" 
          type="date"
          :error="form.errors.from"
        />
        <text-input 
          v-model="form.to" 
          label="Fecha final" 
          type="date"
          :error="form.errors.to"
        />
        <select-input 
          v-model="form.modality" 
          label="Tipo de reporte"
          :error="form.errors.modality"
        >
          <option value="">Seleccionar tipo</option>
          <option value="advance">Reporte detallado</option>
          <option value="simple">Reporte simple</option>
        </select-input>
        <select-input 
          v-model="form.nulled" 
          label="Incluir anulados"
          :error="form.errors.nulled"
        >
          <option value="">Seleccionar opción</option>
          <option value="1">Incluir anulados</option>
          <option value="0">No incluir anulados</option>
        </select-input>
        <select-input 
          v-model="form.type" 
          label="Formato de descarga"
          :error="form.errors.type"
        >
          <option value="excel">Excel</option>
        </select-input>
        <div class="flex items-end">
          <button 
            class="btn-blue w-full flex items-center justify-center gap-2" 
            @click="generateReport()"
            :disabled="form.processing"
          >
            <i class="fa fa-file-export"></i>
            <span>Generar reporte</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Reportes por depósito -->
    <div class="bg-white p-6 rounded-lg shadow-sm mb-6">
      <h2 class="text-xl font-semibold mb-4">Productos por Depósito</h2>
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <button
          v-for="deposit in deposits"
          :key="deposit.id"
          class="btn-blue text-center"
          @click="openReportModal(deposit)"
        >
          {{ deposit.name }}
        </button>
      </div>
      <!-- Botón para reporte completo -->
      <div class="mt-6">
        <button
          class="btn-blue w-full md:w-auto flex items-center justify-center gap-2"
          @click="openAllDepositsModal"
        >
          <i class="fa fa-file-excel"></i>
          <span>Generar Reporte Completo (Todos los Depósitos)</span>
        </button>
      </div>
      <!-- Modal para opciones de reporte -->
      <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
          <button class="absolute top-2 right-2 text-gray-500 hover:text-gray-700" @click="showModal = false">
            <i class="fa fa-times"></i>
          </button>
          <h3 class="text-lg font-semibold mb-4">Opciones de Reporte</h3>
          <div class="mb-4">
            <label class="block mb-1 font-medium">Tipo de reporte</label>
            <select v-model="reportForm.type" class="w-full border rounded px-3 py-2">
              <option value="pdf">PDF</option>
              <option value="excel">Excel</option>
            </select>
          </div>
          <div class="mb-4">
            <label class="block mb-1 font-medium">¿Qué mostrar?</label>
            <select v-model="reportForm.show" class="w-full border rounded px-3 py-2">
              <option value="cost">Solo costo</option>
              <option value="cost_price">Costo y precios</option>
            </select>
          </div>
          <div class="flex gap-2">
            <button class="btn-blue flex-1" @click="generateDepositReport">Generar</button>
            <button class="btn-gray flex-1" @click="showModal = false">Cancelar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para reporte completo de todos los depósitos -->
    <div v-if="showAllDepositsModal" class="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
        <button class="absolute top-2 right-2 text-gray-500 hover:text-gray-700" @click="showAllDepositsModal = false">
          <i class="fa fa-times"></i>
        </button>
        <h3 class="text-lg font-semibold mb-4">Opciones de Reporte Completo</h3>
        <div class="mb-4">
          <label class="block mb-1 font-medium">Tipo de reporte</label>
          <select v-model="allDepositsForm.type" class="w-full border rounded px-3 py-2">
            <option value="excel">Excel</option>
          </select>
        </div>
        <div class="mb-4">
          <label class="block mb-1 font-medium">¿Qué mostrar?</label>
          <select v-model="allDepositsForm.show" class="w-full border rounded px-3 py-2">
            <option value="cost">Solo costo</option>
            <option value="cost_price">Costo y precios</option>
          </select>
        </div>
        <div class="flex gap-2">
          <button class="btn-blue flex-1" @click="generateAllDepositsReport">Generar</button>
          <button class="btn-gray flex-1" @click="showAllDepositsModal = false">Cancelar</button>
        </div>
      </div>
    </div>

    <!-- Reporte de productos -->
    <div class="bg-white p-6 rounded-lg shadow-sm mb-6">
      <h2 class="text-xl font-semibold mb-4">Lista de Productos</h2>
      <button 
        @click="print()" 
        class="btn-blue flex items-center gap-2"
      >
        <i class="fa fa-print"></i>
        <span>Imprimir lista de precios</span>
      </button>
    </div>

    <!-- Link de descarga -->
    <div v-if="count" class="bg-green-50 p-4 rounded-lg mb-6">
      <div class="flex items-center gap-2 text-green-700">
        <i class="fa fa-check-circle"></i>
        <span>¡Reporte generado!</span>
        <a 
          :href="`/orders-${form.orderNumber}.xlsx`"
          class="ml-2 text-green-800 hover:text-green-900 underline"
        >
          Descargar archivo
        </a>
      </div>
    </div>

    <!-- Productos por agotarse -->
    <div v-if="role == 'admin' || role == 'storer'" class="bg-white p-6 rounded-lg shadow-sm">
      <h2 class="text-xl font-semibold mb-4">Productos con Stock Bajo</h2>
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="text-left border-b">
              <th class="pb-3 px-4">Código</th>
              <th class="pb-3 px-4">Nombre del Producto</th>
              <th class="pb-3 px-4">Stock Actual</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="prod in productsSoldout" :key="prod.id" class="border-b hover:bg-gray-50">
              <td class="py-3 px-4">{{ prod.code }}</td>
              <td class="py-3 px-4">{{ prod.name }}</td>
              <td class="py-3 px-4">
                <span class="text-red-600 font-medium">{{ prod.quantity }}</span>
              </td>
            </tr>
            <tr v-if="!productsSoldout?.length">
              <td class="p-4 text-center text-gray-500" colspan="3">
                No hay productos con stock bajo en este momento.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="bg-white rounded-lg shadow-sm p-6 mb-6">
      <h2 class="text-xl font-semibold mb-4">Reporte de Comisiones de Ventas</h2>
      <p class="text-gray-600 mb-4">
        Genera un reporte detallado de las comisiones por vendedor basado en las ventas realizadas en un rango de fechas. Incluye sección de firmas para El Responsable y El Vendedor como soporte de pago.
      </p>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <text-input
          v-model="commissionForm.from"
          label="Fecha inicial"
          type="date"
          :error="commissionForm.errors.from"
        />
        <text-input
          v-model="commissionForm.to"
          label="Fecha final"
          type="date"
          :error="commissionForm.errors.to"
        />
        <select-input
          v-model="commissionForm.vendedor"
          label="Vendedor"
          :error="commissionForm.errors.vendedor"
        >
          <option value="">Todos los vendedores</option>
          <option v-for="vend in vendedores" :key="vend.id" :value="vend.id">
            {{ vend.first_name }} {{ vend.last_name }}
          </option>
        </select-input>
        <select-input
          v-model="commissionForm.format"
          label="Formato de descarga"
          :error="commissionForm.errors.format"
        >
          <option value="excel">Excel (.xlsx)</option>
          <option value="pdf">PDF (Para imprimir y firmar)</option>
        </select-input>
      </div>
      <div class="mt-4 flex justify-end">
        <button
          class="btn-blue flex items-center justify-center gap-2 px-6 py-2.5"
          @click="generateCommissionReport()"
          :disabled="commissionForm.processing || generatingCommission"
        >
          <i :class="commissionForm.format === 'pdf' ? 'fa fa-file-pdf' : 'fa fa-file-excel'"></i>
          <span>{{ generatingCommission ? 'Generando reporte...' : 'Generar Reporte de Comisiones' }}</span>
        </button>
      </div>
    </div>

    <div class="bg-white rounded-lg shadow-sm p-6">
      <h2 class="text-xl font-semibold mb-4">Catálogo de Productos</h2>
      <p class="text-gray-600 mb-4">
        Descarga un catálogo completo de todos los productos disponibles en el sistema, incluyendo precios y stock actual.
      </p>
      <a
        :href="`/reports/product-catalog`"
        class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
        Descargar Catálogo
      </a>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import axios from 'axios'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput.vue'
import SelectInput from "@/Shared/SelectInput.vue"

export default {
  components: {
    SelectInput,
    TextInput,
    Head,
    Link,
  },
  layout: Layout,
  props: {
    order: String,
    deposits: {
      type: Object,
      required: true
    },
    role: {
      type: String,
      required: true
    },
    productsSoldout: {
      type: Array,
      default: () => []
    },
    vendedores: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      form: this.$inertia.form({
        from: '',
        to: '',
        type: 'excel',
        full: false,
        nulled: '0',
        modality: 'simple',
        orderNumber: this.generateRandomNumber()
      }),
      count: 0,
      showModal: false,
      selectedDeposit: null,
      reportForm: {
        type: 'pdf',
        show: 'cost',
      },
      showAllDepositsModal: false,
      allDepositsForm: {
        type: 'excel',
        show: 'cost',
      },
      commissionForm: this.$inertia.form({
        from: '',
        to: '',
        vendedor: '',
        format: 'excel',
      }),
      generatingCommission: false,
    }
  },
  methods: {
    generateRandomNumber() {
      return Math.floor(Math.random() * (59999 - 10) + 10)
    },
    print() {
      window.open('/products/thermical', 'Precios de productos', 'width=400,height=500,toolbar=no')
    },
    openReportModal(deposit) {
      this.selectedDeposit = deposit;
      this.showModal = true;
      this.reportForm = {
        type: 'pdf',
        show: 'cost',
      };
    },
    generateDepositReport() {
      if (!this.selectedDeposit) return;
      const params = new URLSearchParams({
        type: this.reportForm.type,
        show: this.reportForm.show
      }).toString();
      const url = `/products/${this.selectedDeposit.id}/deposits/report?${params}`;
      window.open(url, '_blank');
      this.showModal = false;
    },
    openAllDepositsModal() {
      this.showAllDepositsModal = true;
      this.allDepositsForm = {
        type: 'excel',
        show: 'cost',
      };
    },
    generateAllDepositsReport() {
      const params = new URLSearchParams({
        type: this.allDepositsForm.type,
        show: this.allDepositsForm.show
      }).toString();
      const url = `/products/all-deposits/report?${params}`;
      window.open(url, '_blank');
      this.showAllDepositsModal = false;
    },
    async generateReport() {
      try {
        await this.form.post('/orders/report')
        this.count = 1
        this.form.orderNumber = this.generateRandomNumber()
      } catch (error) {
        console.error('Error al generar reporte:', error)
      }
    },
    async generateCommissionReport() {
      if (!this.commissionForm.from || !this.commissionForm.to) {
        alert('Por favor seleccione las fechas inicial y final');
        return;
      }
      
      this.generatingCommission = true;
      try {
        const isPdf = this.commissionForm.format === 'pdf';
        const response = await axios.post('/orders/commission-report', {
            from: this.commissionForm.from,
            to: this.commissionForm.to,
            vendedor: this.commissionForm.vendedor,
            format: this.commissionForm.format || 'excel'
        }, {
            responseType: 'blob'
        });
        
        const blobType = isPdf ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        const ext = isPdf ? 'pdf' : 'xlsx';
        const url = window.URL.createObjectURL(new Blob([response.data], { type: blobType }));
        const link = document.createElement('a');
        link.href = url;
        const filename = `commissions-${this.commissionForm.from}-to-${this.commissionForm.to}.${ext}`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error al generar reporte de comisiones:', error);
        alert('Hubo un error al generar el reporte.');
      } finally {
        this.generatingCommission = false;
      }
    }
  }
}
</script>
